import { ProductCard, ProductActions } from "./ToolsMobileCards";
import { useToolsMobile } from "./ToolsLayout";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Search } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import ToolsLayout, { ListFooter, RefreshTools, ToolsState } from "./ToolsLayout";
import useToolsData from "./useToolsData";
import useApiEp from "../hooks/useApiEp";
import { categoryNames, invalidateTools, money, number, stockState } from "./toolsData";
import StockDialog from "./StockDialog";
import ExportExcelButton from "../lists/ExportExcelButton";

const stockLabels = {
  out: "Out of stock",
  low: "Low stock",
  healthy: "In stock",
  unknown: "Stock unavailable",
};
const columns = [
  { label: "Product", value: (row) => row.name },
  { label: "Category", value: (row) => row.category },
  {
    label: "Stock",
    value: (row) => (stockState(row.stock) === "unknown" ? "" : Number(row.stock)),
  },
  { label: "MRP (INR)", value: (row) => number(row.mrp) },
  { label: "Selling price (INR)", value: (row) => number(row.sellingPrice) },
];
export default function ToolsPage() {
  const mobile = useToolsMobile();
  const [params, setParams] = useSearchParams();
  const query = useToolsData("products"),
    categoriesQuery = useApiEp("user/product-categories");
  const client = useQueryClient();
  const [page, setPage] = useState(1),
    [stockProduct, setStockProduct] = useState(null),
    [deleting, setDeleting] = useState(null),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  const search = params.get("search") || "",
    category = params.get("category") || "",
    stock = params.get("stock") || "";
  const updateFilter = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete("product");
    setParams(next, { replace: true });
    setPage(1);
  };
  const products = useMemo(() => query.data || [], [query.data]);
  const categories = [
    ...new Set([
      ...categoryNames(categoriesQuery.data),
      ...products.map((row) => row.category).filter(Boolean),
    ]),
  ].sort();
  const filtered = useMemo(
    () =>
      products.filter(
        (row) =>
          (!search ||
            `${row.name || ""} ${row.description || ""}`
              .toLowerCase()
              .includes(search.toLowerCase())) &&
          (!category || row.category === category) &&
          (!stock ||
            (stock === "attention"
              ? ["low", "out"].includes(stockState(row.stock))
              : stockState(row.stock) === stock)),
      ),
    [products, search, category, stock],
  );
  const visible = filtered.slice((page - 1) * 10, page * 10);
  const hasFilters = Boolean(search || category || stock);
  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(1, Math.ceil(filtered.length / 10))));
  }, [filtered.length]);
  useEffect(() => {
    const selected = (query.data || []).find((row) => row._id === params.get("product"));
    if (selected) setStockProduct(selected);
  }, [query.data, params]);
  const reset = () => {
    setParams({}, { replace: true });
    setPage(1);
  };
  const closeStock = () => {
    setStockProduct(null);
    const next = new URLSearchParams(params);
    next.delete("product");
    setParams(next, { replace: true });
  };
  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.delete(
        `${API_ENDPOINT}admin/delete-tool/${encodeURIComponent(deleting._id)}`,
        getHeader(),
      );
      await invalidateTools(client);
      setNotice(`${deleting.name} deleted.`);
      setDeleting(null);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to delete this product. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ToolsLayout
      title="Products & inventory"
      description="Maintain your catalogue and keep stock counts up to date."
      actions={
        <>
          <RefreshTools onClick={query.refetch} loading={query.isFetching} />
          <Button variant="contained" component={Link} to="/home/add-tool" startIcon={<Add />}>
            Add product
          </Button>
        </>
      }
    >
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      <section className="tools-metrics" aria-label="Inventory summary">
        {[
          ["Products", products.length, "All catalogue products"],
          [
            "Available units",
            products.some((row) => stockState(row.stock) === "unknown")
              ? "—"
              : products.reduce((sum, row) => sum + number(row.stock), 0),
            "Stock on hand across all products",
          ],
          [
            "Low stock",
            products.filter((row) => stockState(row.stock) === "low").length,
            "1–10 units remaining",
          ],
          [
            "Out of stock",
            products.filter((row) => stockState(row.stock) === "out").length,
            "Products with zero available units",
          ],
        ].map(([label, value, caption]) => (
          <div className="tools-metric" key={label}>
            <span>{label}</span>
            <strong>{query.isLoading ? "…" : query.isError ? "—" : value}</strong>
            <p>{caption}</p>
          </div>
        ))}
      </section>
      <section className="tools-panel">
        <div className="tools-panel-heading">
          <div>
            <Typography variant="h6" component="h2">
              Product catalogue
            </Typography>
            <p>Search, edit details, or update stock without leaving the list.</p>
          </div>
          <ExportExcelButton
            columns={columns}
            filename="silkcart-tools-inventory"
            sheetName="Inventory"
            disabled={query.isLoading || query.isError}
            loadRows={async () => filtered}
          />
        </div>
        <div className="tools-filters">
          <TextField
            size="small"
            label="Search products"
            value={search}
            onChange={(event) => updateFilter("search", event.target.value)}
            InputProps={{
              startAdornment: <Search fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="tool-category">Category</InputLabel>
            <Select
              labelId="tool-category"
              label="Category"
              value={category}
              onChange={(event) => updateFilter("category", event.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {[...new Set([...categories, ...(category ? [category] : [])])].map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            select
            size="small"
            label="Stock availability"
            value={stock}
            onChange={(event) => updateFilter("stock", event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">All stock</MenuItem>
            <MenuItem value="attention">Needs replenishment</MenuItem>
            {Object.entries(stockLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {hasFilters && <Button onClick={reset}>Clear filters</Button>}
        </div>
        <ToolsState
          loading={query.isLoading}
          error={query.isError}
          retry={query.refetch}
          empty={!filtered.length}
          emptyTitle={hasFilters ? "No matching products" : "Add your first product"}
          emptyMessage={
            hasFilters
              ? "Try another search or clear the filters."
              : "Create a product with pricing, images, and opening stock."
          }
          action={
            hasFilters ? (
              <Button onClick={reset}>Clear filters</Button>
            ) : (
              <Button component={Link} to="/home/add-tool">
                Add product
              </Button>
            )
          }
        >
          {mobile ? (
            <div>
              {visible.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onStock={(product) => {
                    setError("");
                    setStockProduct(product);
                  }}
                  onDelete={(product) => {
                    setError("");
                    setDeleting(product);
                  }}
                />
              ))}
            </div>
          ) : (
            <TableContainer>
              <Table aria-label="Tools inventory">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Stock on hand</TableCell>
                    <TableCell align="right">Selling price / MRP</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((product) => {
                    const state = stockState(product.stock);
                    return (
                      <TableRow key={product._id} hover>
                        <TableCell>
                          <div className="tools-product">
                            {(product.images?.[0] || product.imageUrl) && (
                              <img src={product.images?.[0] || product.imageUrl} alt="" />
                            )}
                            <div>
                              <Typography variant="body2" fontWeight={800}>
                                {product.name}
                              </Typography>
                              <small>
                                {product.weight
                                  ? `${product.weight} ${product.weightUnit || "kg"}`
                                  : "No unit weight specified"}
                              </small>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{product.category || "Uncategorised"}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800}>
                            {state === "unknown" ? "—" : `${product.stock} units`}
                          </Typography>
                          <Chip
                            sx={{ mt: 0.5 }}
                            size="small"
                            variant="outlined"
                            color={
                              state === "healthy"
                                ? "success"
                                : state === "low"
                                  ? "warning"
                                  : state === "out"
                                    ? "error"
                                    : "default"
                            }
                            label={stockLabels[state]}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" fontWeight={800}>
                            {money(product.sellingPrice)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            MRP {money(product.mrp)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 255 }}>
                            <ProductActions
                              product={product}
                              onStock={(product) => {
                                setError("");
                                setStockProduct(product);
                              }}
                              onDelete={(product) => {
                                setError("");
                                setDeleting(product);
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ListFooter page={page} count={filtered.length} pageSize={10} setPage={setPage} />
        </ToolsState>
      </section>
      {stockProduct && (
        <StockDialog
          key={stockProduct._id}
          product={stockProduct}
          onClose={closeStock}
          onSaved={setNotice}
        />
      )}
      <Dialog
        open={Boolean(deleting)}
        onClose={busy ? undefined : () => setDeleting(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete product?</DialogTitle>
        <DialogContent>
          <Typography>“{deleting?.name}” will be removed from the catalogue.</Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton loading={busy} color="error" variant="contained" onClick={remove}>
            Delete product
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </ToolsLayout>
  );
}
